// Per-source sync cursor read/write (ADR 0032).
//
// Foundation for Sales Nav (1.9) and Recruiter (1.10) parallel
// inboxes. Every source (`"salesnav_inbox"`, `"salesnav_archive"`,
// `"recruiter_inbox"`, ...) has its own cursor row — provider-
// specific JSON plus a status tri-state (`idle` / `running` /
// `error`).
//
// This hook is deliberately thin:
//
//   * `cursor` — the parsed cursor object (`null` before the first
//     sync).
//   * `status`, `lastSyncedAt`, `error` — stamped by the worker that
//     owns the sync loop.
//   * `advance({cursor, status?, error?})` — upserts the row with
//     optimistic cache update. Passing `cursor` is required; status
//     / error are convenience write-throughs for the worker that
//     just finished a tick.
//
// The worker itself lives elsewhere (a polling-framework tick for
// Sales Nav + Recruiter). This module only owns the durable
// storage.

import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Row } from '../lib/db-types'

/** Known sync sources. Free-form in Postgres (`text` column) but we
 * constrain the TypeScript union so callers can't typo. Add new
 * entries here alongside the feature that consumes them. */
export type SyncCursorSource =
  | 'salesnav_inbox'
  | 'salesnav_archive'
  | 'recruiter_inbox'
  | 'recruiter_archive'

export type SyncCursorStatus = 'idle' | 'running' | 'error'

export interface SyncCursor {
  id: string
  source: SyncCursorSource
  cursor: unknown
  status: SyncCursorStatus
  lastSyncedAt: string | null
  error: string | null
}

export const syncCursorKey = (
  userId: string | undefined,
  source: SyncCursorSource,
): QueryKey => ['sync_cursors', userId ?? 'anon', source]

type CursorRow = Pick<
  Row<'sync_cursors'>,
  'id' | 'source' | 'cursor' | 'status' | 'last_synced_at' | 'error'
>

function rowToCursor(row: CursorRow): SyncCursor {
  return {
    id: row.id,
    source: row.source as SyncCursorSource,
    cursor: row.cursor,
    status: row.status as SyncCursorStatus,
    lastSyncedAt: row.last_synced_at,
    error: row.error,
  }
}

export function useSyncCursor(source: SyncCursorSource) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = syncCursorKey(user?.id, source)

  const query = useQuery<SyncCursor | null>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sync_cursors')
        .select('id, source, cursor, status, last_synced_at, error')
        .eq('source', source)
        .maybeSingle()
      if (error) throw new Error(error.message)
      return data ? rowToCursor(data as CursorRow) : null
    },
  })

  const advanceMut = useMutation({
    mutationKey: ['sync_cursors', 'advance', source],
    mutationFn: async (input: {
      cursor: unknown
      status?: SyncCursorStatus
      error?: string | null
    }): Promise<SyncCursor> => {
      if (!user) throw new Error('Not authenticated')
      const nextStatus: SyncCursorStatus = input.status ?? 'idle'
      const nextError: string | null = input.error ?? null

      // Optimistic update so the next useSyncCursor read sees the new
      // cursor without waiting for the round-trip. On failure we
      // rollback to the pre-update snapshot.
      const snapshot = qc.getQueryData<SyncCursor | null>(key)
      qc.setQueryData<SyncCursor | null>(key, (prev) => ({
        id: prev?.id ?? 'tmp-cursor',
        source,
        cursor: input.cursor,
        status: nextStatus,
        lastSyncedAt: new Date().toISOString(),
        error: nextError,
      }))

      const { data, error } = await supabase
        .from('sync_cursors')
        .upsert(
          {
            user_id: user.id,
            source,
            // Cast through unknown for the generated jsonb type — any
            // JSON-safe value is valid on write.
            cursor: input.cursor as unknown as Row<'sync_cursors'>['cursor'],
            status: nextStatus,
            error: nextError,
            last_synced_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,source' },
        )
        .select('id, source, cursor, status, last_synced_at, error')
        .single()

      if (error) {
        qc.setQueryData(key, snapshot)
        throw new Error(error.message)
      }
      const next = rowToCursor(data as CursorRow)
      qc.setQueryData(key, next)
      return next
    },
  })

  return {
    cursor: query.data ?? null,
    loading: !!user && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    advance: (input: { cursor: unknown; status?: SyncCursorStatus; error?: string | null }) =>
      advanceMut.mutateAsync(input),
  }
}
